import { History } from "history";
import { equals } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import { getDocumentsPollInterval } from "src/configState/selector";
import { actions, RequestProgressState } from "src/requestProgress/reducer";
import { getRequestProgress } from "src/requestProgress/selector";
import { isRequesting, transitionIfFinished } from "../requestProgress/helpers";
import { cacheSearch, deleteCachedSearch } from "../searchCache/actions";
import LoadingSpinner from "../shared/LoadingSpinner";
import selectorsToProps from "../utils/selectorsToProps";
import {
  loadBriefSpreads,
  LoadBriefSpreadsActionType,
  reloadBriefSpreads,
  ReloadBriefSpreadsActionType,
  resetBriefSpread
} from "./actions";
import DocumentsList from "./DocumentsList";
import {
  getBriefSpreadFilter,
  getBriefSpreadPage,
  getBriefSpreadQuery,
  getBriefSpreadSort,
  getBriefSpreadStatusGroup,
  getPollingEnabled
} from "./selector";
import { SpreadFilter, SpreadSort, SpreadStatusGroup } from "./types";

interface DocumentsProps {
  loadBriefSpreads: LoadBriefSpreadsActionType;
  reloadBriefSpreads: ReloadBriefSpreadsActionType;
  spreadSort: SpreadSort;
  history: History;
  requestProgress: RequestProgressState;
  resetBriefSpread: typeof resetBriefSpread;
  pollIntervalMs: number;
  page?: number;
  spreadFilter: SpreadFilter;
  spreadStatusGroup: SpreadStatusGroup;
  searchTerm?: string;
  cacheSearchFunc: typeof cacheSearch;
  deleteCachedSearchFunc: typeof deleteCachedSearch;
  pollingEnabled: boolean;
}

export class Documents extends React.Component<DocumentsProps> {
  public pollTimeout: number | undefined;
  public isLoadingBriefSpread: boolean = false;
  public hasLoadedBriefSpread: boolean = false;

  constructor(props: DocumentsProps) {
    super(props);
  }

  public componentDidMount() {
    this.shouldLoadBriefSpreads();
  }

  public componentWillReceiveProps(nextProps: DocumentsProps) {
    // if any sort/page/filters change, lets reload the spread list
    if (
      !equals(nextProps.spreadSort, this.props.spreadSort) ||
      !equals(nextProps.spreadFilter, this.props.spreadFilter) ||
      !equals(nextProps.page, this.props.page) ||
      !equals(nextProps.spreadStatusGroup, this.props.spreadStatusGroup)
    ) {
      // cache the search if a searchTerm exists
      if (nextProps.searchTerm) {
        this.props.cacheSearchFunc({
          spreadFilter: nextProps.spreadFilter,
          spreadStatusGroup: nextProps.spreadStatusGroup,
          spreadSort: nextProps.spreadSort,
          query: nextProps.searchTerm,
          page: nextProps.page
        });
      }

      clearTimeout(this.pollTimeout);
      this.reloadList();
    }
    this.shouldLoadBriefSpreads();
  }

  public componentWillUnmount() {
    this.props.resetBriefSpread();
    clearTimeout(this.pollTimeout);
  }

  public reloadList = () => {
    if (this.props.pollingEnabled) {
      this.props.reloadBriefSpreads();

      this.pollTimeout = window.setTimeout(
        this.reloadList,
        this.props.pollIntervalMs
      );
    }
  };

  public performSearch = () => {
    clearTimeout(this.pollTimeout);
    this.resetSearch();
  };

  public stopPolling = () => {
    clearTimeout(this.pollTimeout);
  };

  public startPolling = () => {
    this.pollTimeout = window.setTimeout(
      this.reloadList,
      this.props.pollIntervalMs
    );
  };

  public resetSearch = () => {
    this.props.deleteCachedSearchFunc({});
    this.reloadList();
  };

  public render() {
    return this.hasLoadedBriefSpread ? (
      <DocumentsList
        history={this.props.history}
        performSearch={this.performSearch}
        resetSearch={this.resetSearch}
        stopPolling={this.stopPolling}
        startPolling={this.startPolling}
      />
    ) : (
      <LoadingSpinner />
    );
  }

  private initializeBriefSpread() {
    this.isLoadingBriefSpread = true;
    this.props.loadBriefSpreads();
    this.pollTimeout = window.setTimeout(
      this.reloadList,
      this.props.pollIntervalMs
    );
  }

  private shouldLoadBriefSpreads() {
    const isFinishedLoadingBriefSpreads = transitionIfFinished(
      actions.loadBriefSpreads
    );
    if (isFinishedLoadingBriefSpreads && isFinishedLoadingBriefSpreads.loaded) {
      this.hasLoadedBriefSpread = true;
      return;
    }

    if (this.hasLoadedBriefSpread || this.isLoadingBriefSpread) {
      return;
    }

    const isComingFromSpreadView = isRequesting(actions.unlockSpread);

    if (!isComingFromSpreadView) {
      this.initializeBriefSpread();
      return;
    }

    const isFinishedUnlockingSpread = transitionIfFinished(
      actions.unlockSpread
    );
    if (isFinishedUnlockingSpread && isFinishedUnlockingSpread.finished) {
      this.initializeBriefSpread();
      return;
    }
  }
}

export default connect(
  selectorsToProps({
    spreadSort: getBriefSpreadSort,
    requestProgress: getRequestProgress,
    pollIntervalMs: getDocumentsPollInterval,
    page: getBriefSpreadPage,
    spreadFilter: getBriefSpreadFilter,
    spreadStatusGroup: getBriefSpreadStatusGroup,
    searchTerm: getBriefSpreadQuery,
    pollingEnabled: getPollingEnabled
  }),
  {
    resetBriefSpread,
    loadBriefSpreads: loadBriefSpreads.action,
    reloadBriefSpreads: reloadBriefSpreads.action,
    cacheSearchFunc: cacheSearch,
    deleteCachedSearchFunc: deleteCachedSearch
  }
)(Documents);



// WEBPACK FOOTER //
// ./src/documentList/Documents.tsx